import { NotificationType } from './notifications';

export const GROUPED_NOTIFICATION_SETTINGS = [
  {
    id: 'shiftSwapStatus',
    title: 'Shift swap status',
    description: 'Notifications about shift swap requests, acceptances, and declines',
    notificationTypes: [NotificationType.swapRequested, NotificationType.swapAccepted, NotificationType.swapDeclined],
  },
  {
    id: 'changesToShifts',
    title: 'Changes to your shifts',
    description: 'Notifications when shifts are assigned, unassigned, or updated',
    notificationTypes: [NotificationType.removedFromShift, NotificationType.addedToShift, NotificationType.shiftUpdate],
  },
  {
    id: 'shiftStartEndReminders',
    title: 'Shift start & end reminders',
    description: 'Reminders when shifts are about to start or end',
    notificationTypes: [NotificationType.shiftStart, NotificationType.shiftEnd],
  },
  {
    id: 'violationWarnings',
    title: 'Violation warnings',
    description: 'Warnings about violations that will occur soon if you do not take action',
    notificationTypes: [
      NotificationType.lateMealBreakEmployeeWarning,
      NotificationType.longBreakEmployeeWarning,
      NotificationType.missedRestBreakWindowEmployeeWarning,
    ],
  },
  {
    id: 'violations',
    title: 'Violations',
    description: 'Notifications about violations being created or updated',
    notificationTypes: [NotificationType.violationCreated, NotificationType.violationUpdated],
  },
  {
    id: 'newSchedulePublished',
    title: 'New schedule published',
    description: 'Notifications when a new schedule is published',
    notificationTypes: [NotificationType.newSchedulePublished],
  },
  {
    id: 'employeeViolation',
    title: 'Employee violation warnings',
    description: 'Warnings about employee violations that will occur soon if you do not take action',
    notificationTypes: [
      NotificationType.lateMealBreakManagerWarning,
      NotificationType.longBreakManagerWarning,
      NotificationType.missedRestBreakWindowManagerWarning,
    ],
    isPrivileged: true,
  },
  {
    id: 'employeeBreakWaived',
    title: 'Employee break waived',
    description: 'Notifications when an employee waives a break',
    notificationTypes: [NotificationType.breakWaived],
    isPrivileged: true,
  },
  {
    id: 'employeeStartedOrEndedShift',
    title: 'Employee shift started or ended',
    description: 'Notifications when an employee starts or ends a shift',
    notificationTypes: [NotificationType.clockedIn, NotificationType.clockedOut],
    isPrivileged: true,
  },
  {
    id: 'employeeStartedOrEndedMealBreak',
    title: 'Employee meal break started or ended',
    description: 'Notifications when an employee starts or ends a meal break',
    notificationTypes: [NotificationType.mealBreakStart, NotificationType.mealBreakEnd],
    isPrivileged: true,
  },
];

// Helper function to get all notification types from grouped settings
export const getAllNotificationTypes = () => {
  return GROUPED_NOTIFICATION_SETTINGS.flatMap((group) => group.notificationTypes);
};

// Helper function to check if a notification type is enabled based on grouped settings
export const isNotificationTypeEnabled = (notificationType, groupedSettings) => {
  const group = GROUPED_NOTIFICATION_SETTINGS.find((g) => g.notificationTypes.includes(notificationType));
  return group ? groupedSettings[group.id] : true;
};

// Helper function to convert individual settings to grouped settings
export const convertToGroupedSettings = (individualSettings) => {
  const groupedSettings = {};

  // Define which notification types should default to false (matching backend)
  const disabledByDefault = [
    NotificationType.clockedIn,
    NotificationType.clockedOut,
    NotificationType.mealBreakStart,
    NotificationType.mealBreakEnd,
  ];

  GROUPED_NOTIFICATION_SETTINGS.forEach((group) => {
    const allEnabled = group.notificationTypes.every((type) => {
      // If individualSettings is null/undefined, check if this type should be disabled by default
      if (!individualSettings) {
        return !disabledByDefault.includes(type);
      }
      // If the type is explicitly set, use that value
      if (individualSettings.hasOwnProperty(type)) {
        return individualSettings[type];
      }
      // If the type is not set, check if it should be disabled by default
      return !disabledByDefault.includes(type);
    });
    groupedSettings[group.id] = allEnabled;
  });

  return groupedSettings;
};

// Helper function to convert grouped settings to individual settings
export const convertToIndividualSettings = (groupedSettings) => {
  const individualSettings = {};

  GROUPED_NOTIFICATION_SETTINGS.forEach((group) => {
    const isEnabled = groupedSettings[group.id];
    group.notificationTypes.forEach((type) => {
      individualSettings[type] = isEnabled;
    });
  });

  return individualSettings;
};
